import CardCommon from '@op/components/CardCommon';
import {
  barIcon,
  lineIcon,
  pieIcon,
  stackLineIcon,
  SymbolType,
  tableIcon,
  vsIcon,
} from '@op/constants/chart';
import DetailWrapper from '@op/pages/Detail/component/DetailWrapper';
import usePcConsoleTimelineChart from '@op/pages/gamestore/hooks/usePcConsoleTimelineChart';
import { decimalFormatPercentage } from '@op/utils/value';
import React, { useMemo, useState } from 'react';
import MentionsTable from './MentionsTable';
import MentionsPieCharts from './MentionsPieCharts';
import MentionsLineBarCharts from './MentionsLineBarCharts';
import { formatDateFunc } from '@op/utils/time';
import { arrayToSimpleObj, sortTimeListFunc } from '@op/utils';
import { useHistory } from 'umi';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import moment from 'moment';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';

function getTableColumns(chartType: SymbolType) {
  const common: Array<any> = [
    {
      text: 'Positive',
      key: 'select_recommend',
      type: chartType,
      color: '#69D19C',
      valueKey: 'review',
    },
    {
      text: 'Negative',
      key: 'select_non_recommend',
      type: chartType,
      color: '#F17C86',
      valueKey: 'review',
    },
  ];

  if (chartType === 'bar' || chartType === 'table') {
    common.unshift({
      text: '30-Day Positive %',
      key: 'recent_reviews',
      type: 'line',
      color: '#5086F3',
      valueKey: 'score',
      numFmt: ['0%'],
      format: (value: number) => decimalFormatPercentage(value, '-', 0),
    });
  }

  return common;
}

interface ISteamChart {
  filter: any;
  trendTimeline: any;
  gameData: any;
  specialHeight?: number;
  steamInfo: any;
  isDetail?: boolean;
  updateDetailFilter?: (filter: any) => void;
  defaultChartType?: SymbolType;
  detailPage?: boolean;
  contentCard?: boolean;
  defaultSelected?: any;
}
export default function SteamChart(props: ISteamChart) {
  const {
    filter,
    trendTimeline,
    gameData,
    specialHeight = 0,
    steamInfo,
    isDetail = false,
    updateDetailFilter,
    defaultChartType,
    detailPage,
    contentCard = true,
    defaultSelected,
  } = props;

  const history = useHistory();
  const { download, downloading } = useDownloadParams();
  const [chartType, setChartType] = useState<SymbolType>(
    defaultChartType || 'bar',
  );
  const seriesDataKeys = useMemo(() => {
    return getTableColumns(chartType);
  }, [chartType]);

  const { chartMap, chartNoDataMap } = usePcConsoleTimelineChart({
    platformData: trendTimeline,
    filterParams: filter,
    seriesDataKeys,
  });

  const { emptyDescText, hasEmptyImg, hasEmptyTitle } = useMemo(() => {
    let emptyDescText = 'No Steam data under current filter selections';
    let hasEmptyImg = true;
    let hasEmptyTitle = true;
    const formatDate = (date: moment.MomentInput) => moment(date, 'YYYY-MM-DD');
    if (chartNoDataMap) {
      const { release_time: release } =
        gameData?.[filter?.edition_unified_id] || {};
      const valid =
        release &&
        formatDate(filter?.end_time).diff(formatDate(release), 'days') > 30;
      if (!valid) {
        emptyDescText =
          '“Recent Reviews” refer to reviews from the past 30 days. Therefore, data is only available starting from the 31st day after launch.';
        hasEmptyTitle = false;
        hasEmptyImg = false;
      }
    }

    return {
      emptyDescText,
      hasEmptyImg,
      hasEmptyTitle,
    };
  }, [chartNoDataMap, filter, gameData]);

  const records = useMemo(() => {
    const dateType = filter.date_type;
    const formatDate = formatDateFunc(dateType);
    const records: { [key in string]: any } = seriesDataKeys.reduce(
      (pre, { key, valueKey }) => {
        const selectedData = chartMap?.[key] || [];
        const value = arrayToSimpleObj(selectedData, valueKey, ({ time }) =>
          formatDate(time),
        );
        return { ...pre, [key]: value };
      },
      {},
    );

    const table: { [key in string]: any } = {};
    for (const key in records) {
      const element = records[key] || {};
      Object.keys(element).forEach((time: string) => {
        if (!table[time]) table[time] = { time };

        table[time] = { ...table[time], [key]: element[time] };
      });
    }

    const list = Object.values(table);
    list.sort(sortTimeListFunc('time', 'desc'));

    return list;
  }, [chartMap, seriesDataKeys]);

  function getChartElement(type: SymbolType, eventsDict?: any) {
    if (type === 'table') {
      return (
        <MentionsTable
          seriesDataKeys={seriesDataKeys}
          filter={filter}
          records={records}
          specialHeight={specialHeight}
          eventsDict={eventsDict}
        />
      );
    }

    if (type === 'pie') {
      return (
        <MentionsPieCharts
          data={steamInfo}
          filter={filter}
          specialHeight={specialHeight}
          eventsDict={eventsDict}
          detailPage={detailPage}
          defaultSelected={defaultSelected}
        ></MentionsPieCharts>
      );
    }

    return (
      <MentionsLineBarCharts
        chartMap={chartMap}
        filter={filter}
        seriesDataKeys={seriesDataKeys}
        chartType={chartType}
        pieData={steamInfo}
        specialHeight={specialHeight}
        eventsDict={eventsDict}
        detailPage={detailPage}
        defaultSelected={defaultSelected}
      ></MentionsLineBarCharts>
    );
  }

  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filter, 'Game Store');
    } else {
      setChartType(type || 'bar');
    }
  }
  const { Competitor_Rank_Hide } = useGetSingePageSourceRule({
    keys: ['Competitor_Rank_Hide'],
  });
  function getOperationsList() {
    const list = [pieIcon, tableIcon, lineIcon, stackLineIcon, barIcon];

    if (!Competitor_Rank_Hide) {
      list.unshift(vsIcon);
    }

    return list;
  }

  return (
    <CardCommon
      className="dbo-key-metrics-steam"
      contentCard={contentCard}
      // loading={loading}
      empty={chartNoDataMap}
      emptyHeight={268}
      emptyTitleText={'No Steam data yet'}
      emptyDescText={emptyDescText}
      hasEmptyImg={hasEmptyImg}
      hasEmptyTitle={hasEmptyTitle}
      hasDownload={!chartNoDataMap}
      downloadLoading={downloading}
      operations={getOperationsList()}
      operationInfo={[chartType, changeChartType]}
      handleDownloadOnClick={() => {
        download({
          key: 'KeyMetricsGameStoreSteam',
          fileName: 'Key Metrics Steam Game Store',
          filter,
          auxiliaryParams: {
            tableColumns: getTableColumns('table'),
          },
          data: records,
        });
      }}
    >
      <DetailWrapper
        eventProps={{
          key: 'KeyMetricsSteamChart',
          defaultFilter: {
            ...filter,
            channel_type: 'comments',
            channel: ['steam'],
          },
          defaultProps: {
            ...props,
            contentCard: false,
            defaultChartType: chartType,
          },
          paramsKey: {
            bar: {
              start_time: 'name',
              end_time: 'name',
              reviews_rating: 'seriesName',
            },
            line: {
              start_time: 'name',
              end_time: 'name',
              reviews_rating: 'seriesName',
            },
            stack_line: {
              start_time: 'name',
              end_time: 'name',
              reviews_rating: 'seriesName',
            },
            pie: {
              reviews_rating: 'name',
            },
            table: {
              start_time: 'time',
              end_time: 'time',
            },
          },
          chartType,
        }}
        isDetail={isDetail}
        updateDetailFilter={updateDetailFilter}
        formatParamsKey={(value: string, key: string) => {
          if (key === 'reviews_rating') {
            if (value === '30-Day Positive %') {
              return {
                reviews_rating: [],
              };
            }
            return value.toLocaleLowerCase();
          }
          return value;
        }}
      >
        {({ eventsDict }) => <>{getChartElement(chartType, eventsDict)}</>}
      </DetailWrapper>
    </CardCommon>
  );
}
